import {shallowEqual} from 'react-redux';
import {assert} from 'assert-ts';
import type {CodeListMap} from 'schemaDefinition/types';
import {useLocalization} from 'localization';
import {isLinkedRoleCodeListId} from 'schemaDefinition';
import type {LinkedRoleCodeListId} from 'schemaDefinition/types/linkTypes';
import {selectSettingsPreferredLocale} from 'services/settings';
import {useAppSelector} from 'store/hooks';
import {getLinkedRoleCodelist} from 'schemas/links/functions';
import type {CodeListId} from '../types';
import {selectCodelistFromCache} from '../codelistSelectors';
import {useRestrictedCodelistsContext} from '../contexts';
import {getDummyCodeList} from './getDummyCodeList';

export const useOptionalCodeLists = (
  ids: (CodeListId | LinkedRoleCodeListId)[] | undefined,
): CodeListMap | undefined => {
  const localization = useLocalization();
  const language = useAppSelector(selectSettingsPreferredLocale);
  const {getRestrictedCodelist} = useRestrictedCodelistsContext();

  return useAppSelector(
    state => {
      if (!ids) return undefined;

      return ids.reduce<CodeListMap>((acc, id) => {
        if (isLinkedRoleCodeListId(id)) {
          acc[id] = getLinkedRoleCodelist(id, localization);
          return acc;
        }

        const codeList = selectCodelistFromCache(id, language, state);
        if (
          assert.soft(codeList, 'useOptionalCodeLists: Code list not loaded', {
            id,
          })
        ) {
          acc[id] = getRestrictedCodelist(codeList);
        } else {
          acc[id] = getDummyCodeList(id);
        }

        return acc;
      }, {});
    },
    (a, b) =>
      a === undefined || b === undefined ? a === b : shallowEqual(a, b),
  );
};
